import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { listingsAPI, categoriesAPI, settingsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import ListingCard from '@/components/listings/ListingCard';
import { Search, SlidersHorizontal, X, Loader2 } from 'lucide-react';
import { useSEO, SEO_KEYWORDS } from '@/hooks/useSEO';
import { useListingGrid } from '@/hooks/useListingGrid';

// Generate storage key based on route
const getStorageKey = (slug, subcategory, searchQuery) => {
    if (searchQuery) return `infinite_scroll_search_${searchQuery}`;
    if (subcategory) return `infinite_scroll_category_${slug}_${subcategory}`;
    return `infinite_scroll_category_${slug || 'all'}`;
};

const getNavFlagKey = (storageKey) => `nav_away_${storageKey}`;

// Read saved state synchronously - only if user navigated away (not refresh)
const readSavedState = (storageKey) => {
    try {
        const navFlagKey = getNavFlagKey(storageKey);
        const navigatedAway = sessionStorage.getItem(navFlagKey);
        
        // Clear the flag
        sessionStorage.removeItem(navFlagKey);
        
        // Only restore if user navigated away (not on refresh)
        if (!navigatedAway) {
            sessionStorage.removeItem(storageKey);
            return null;
        }
        
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
            const state = JSON.parse(saved);
            if (state && state.listings?.length > 0 && Date.now() - state.timestamp < 10 * 60 * 1000) {
                return state;
            }
        }
    } catch (e) {
        // Ignore
    }
    return null;
};

const CategoryPage = () => {
    const { slug, subcategory } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Get search query from URL
    const searchQuery = searchParams.get('q') || '';
    
    // Generate storage key for this specific route
    const storageKey = getStorageKey(slug, subcategory, searchQuery);
    
    // Track the previous route to detect changes
    const prevRouteRef = useRef(`${slug}-${subcategory}-${searchQuery}`);
    const currentRoute = `${slug}-${subcategory}-${searchQuery}`;
    
    // Read saved state ONCE at initialization
    const savedStateRef = useRef(readSavedState(storageKey));
    const initialState = savedStateRef.current;
    const wasRestored = initialState !== null;
    
    const [category, setCategory] = useState(initialState?.category || null);
    const [listings, setListings] = useState(initialState?.listings || []);
    const [loading, setLoading] = useState(!wasRestored);
    const [loadingMore, setLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(initialState?.currentPage || 1);
    const [totalItems, setTotalItems] = useState(initialState?.totalItems || 0);
    const [hasMore, setHasMore] = useState(initialState?.hasMore ?? true);
    const [itemsPerPage] = useState(24);
    const [filters, setFilters] = useState({
        listing_type: searchParams.get('type') || '',
        min_price: searchParams.get('min') || '',
        max_price: searchParams.get('max') || '',
        condition: searchParams.get('condition') || '',
        sort_by: searchParams.get('sort') || 'created_at',
        sort_order: searchParams.get('order') || 'desc',
    });
    const [showFilters, setShowFilters] = useState(false);
    
    const loadMoreRef = useRef(null);
    const [shouldFetch, setShouldFetch] = useState(!wasRestored);
    const scrollRestoredRef = useRef(false);
    
    // Track the previous slug separately to know when to reset category
    const prevSlugRef = useRef(slug);
    
    // Reset state when route changes (slug, subcategory, or searchQuery)
    useEffect(() => {
        if (prevRouteRef.current !== currentRoute) {
            const slugChanged = prevSlugRef.current !== slug;
            prevRouteRef.current = currentRoute;
            prevSlugRef.current = slug;
            
            // Try to read saved state for new route
            const newSavedState = readSavedState(storageKey);
            
            if (newSavedState) {
                // Restore from saved state
                setListings(newSavedState.listings || []);
                setCurrentPage(newSavedState.currentPage || 1);
                setTotalItems(newSavedState.totalItems || 0);
                setHasMore(newSavedState.hasMore ?? true);
                if (newSavedState.category) {
                    setCategory(newSavedState.category);
                } else if (slugChanged) {
                    // Only reset category if slug changed
                    setCategory(null);
                }
                setLoading(false);
                setShouldFetch(false);
                scrollRestoredRef.current = false;
            } else {
                // Fresh load
                setListings([]);
                setLoading(true);
                setCurrentPage(1);
                setTotalItems(0);
                setHasMore(true);
                // Only reset category if slug changed
                if (slugChanged) {
                    setCategory(null);
                }
                setShouldFetch(true);
                scrollRestoredRef.current = false;
            }
            window.scrollTo(0, 0);
        }
    }, [slug, subcategory, searchQuery, storageKey, currentRoute]);
    
    // Grid settings using shared hook
    const { gridSettings, singleColumn } = useListingGrid();
    
    // SEO for category pages
    const getCategoryKeywords = (categorySlug) => {
        const keywordMap = {
            'numismatica': SEO_KEYWORDS.numismatica,
            'faleristica': SEO_KEYWORDS.faleristica,
            'militare': SEO_KEYWORDS.militare,
            'diverse': SEO_KEYWORDS.diverse
        };
        return keywordMap[categorySlug] || SEO_KEYWORDS.base;
    };
    
    useSEO({
        title: category ? `${category.name} - Piese de Colecție` : (searchQuery ? `Căutare: ${searchQuery}` : 'Categorii'),
        description: category 
            ? `Cumpără și vinde piese de ${category.name.toLowerCase()} pe Colectarium. Licitații și vânzări directe pentru colecționari.`
            : (searchQuery ? `Rezultate căutare pentru "${searchQuery}" pe Colectarium` : 'Explorează categoriile de piese de colecție'),
        keywords: getCategoryKeywords(slug),
        url: `https://colectarium.ro/category/${slug}${subcategory ? '/' + subcategory : ''}`
    });

    // Restore scroll position after mount if we have saved state
    useEffect(() => {
        if (!wasRestored || !initialState?.scrollY || initialState.scrollY <= 0) {
            return;
        }
        
        if (scrollRestoredRef.current) {
            return;
        }
        
        const targetScroll = initialState.scrollY;
        let attempts = 0;
        const maxAttempts = 50;
        
        const attemptScroll = () => {
            attempts++;
            const docHeight = document.documentElement.scrollHeight;
            
            if (docHeight > targetScroll || attempts >= maxAttempts) {
                window.scrollTo({ top: targetScroll, behavior: 'instant' });
                
                setTimeout(() => {
                    const actualScroll = window.scrollY;
                    const diff = Math.abs(actualScroll - targetScroll);
                    
                    if (diff > 100 && attempts < maxAttempts) {
                        requestAnimationFrame(attemptScroll);
                    } else {
                        scrollRestoredRef.current = true;
                    }
                }, 100);
            } else {
                setTimeout(attemptScroll, 100);
            }
        };
        
        const timerId = setTimeout(attemptScroll, 50);
        return () => clearTimeout(timerId);
    }, [wasRestored, initialState?.scrollY, listings.length]);

    // Save state on scroll (debounced) and before navigation
    useEffect(() => {
        if (listings.length === 0) return;
        
        let scrollTimeout;
        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                const state = {
                    listings,
                    currentPage,
                    hasMore,
                    totalItems,
                    category,
                    scrollY: window.scrollY,
                    timestamp: Date.now()
                };
                sessionStorage.setItem(storageKey, JSON.stringify(state));
            }, 200);
        };
        
        // Save immediately on mousedown before navigation
        const handleMouseDown = (e) => {
            const link = e.target.closest('a[href^="/listing/"], a[href^="/category/"], a[href^="/user/"]');
            if (link) {
                // Mark that user is navigating away
                sessionStorage.setItem(getNavFlagKey(storageKey), 'true');
                
                const state = {
                    listings,
                    currentPage,
                    hasMore,
                    totalItems,
                    category,
                    scrollY: window.scrollY,
                    timestamp: Date.now()
                };
                sessionStorage.setItem(storageKey, JSON.stringify(state));
            }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('mousedown', handleMouseDown, { capture: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleMouseDown, { capture: true });
            clearTimeout(scrollTimeout);
        };
    }, [listings, currentPage, hasMore, totalItems, category, storageKey]);

    // Reset pagination when filters change (NOT on initial mount with restored state)
    const filterChangeRef = useRef(false);
    useEffect(() => {
        // Skip first run
        if (!filterChangeRef.current) {
            filterChangeRef.current = true;
            return;
        }
        
        // Clear saved state when filters change
        sessionStorage.removeItem(storageKey);
        setCurrentPage(1);
        setListings([]);
        setHasMore(true);
        setShouldFetch(true);
    }, [filters, storageKey]);

    // Fetch category info (only once when slug changes)
    useEffect(() => {
        const fetchCategory = async () => {
            if (slug && !searchQuery) {
                try {
                    const categoryRes = await categoriesAPI.getBySlug(slug);
                    setCategory(categoryRes.data);
                } catch (error) {
                    console.error('Error fetching category:', error);
                    setCategory(null);
                }
            } else {
                setCategory(null);
            }
        };
        fetchCategory();
    }, [slug, searchQuery]);

    // Fetch listings with infinite scroll
    useEffect(() => {
        // Skip fetch if we shouldn't fetch and it's the first page
        if (!shouldFetch && currentPage === 1) {
            return;
        }
        
        // IMPORTANT: Wait for category to be loaded before fetching listings
        // (unless it's a search query which doesn't need category)
        if (slug && !searchQuery && !category) {
            return; // Wait for category to load first
        }
        
        const fetchData = async () => {
            if (currentPage > 1) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            
            try {
                const skip = (currentPage - 1) * itemsPerPage;
                let params = {
                    skip,
                    limit: itemsPerPage,
                    ...filters,
                };

                // Remove empty params
                Object.keys(params).forEach(key => {
                    if (!params[key]) delete params[key];
                });

                if (searchQuery) {
                    params.q = searchQuery;
                } else if (slug && category) {
                    params.category = category.category_id;
                    
                    if (subcategory) {
                        const sub = category.subcategories?.find(
                            s => s.slug === subcategory || s.name.toLowerCase() === subcategory
                        );
                        if (sub) {
                            params.subcategory = sub.subcategory_id;
                        }
                    }
                }

                const listingsRes = await listingsAPI.getAll(params);
                const data = listingsRes.data;
                const newListings = Array.isArray(data) ? data : (data?.listings || []);
                const total = data?.total || newListings.length;
                
                if (currentPage > 1) {
                    setListings(prev => [...prev, ...newListings]);
                } else {
                    setListings(newListings);
                }
                
                setTotalItems(total);
                setHasMore(skip + newListings.length < total);
                
                // Mark that we should fetch on subsequent page changes
                setShouldFetch(true);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        };

        // Only fetch if we should
        if (shouldFetch) {
            fetchData();
        }
    }, [slug, subcategory, filters, searchQuery, currentPage, itemsPerPage, shouldFetch, category]);

    // Infinite scroll observer
    useEffect(() => {
        if (!hasMore || loading || loadingMore) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    setCurrentPage(prev => prev + 1);
                }
            },
            { threshold: 0.1, rootMargin: '100px' }
        );
        
        if (loadMoreRef.current) {
            observer.observe(loadMoreRef.current);
        }
        
        return () => observer.disconnect();
    }, [hasMore, loading, loadingMore]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        if (value) {
            searchParams.set(key, value);
        } else {
            searchParams.delete(key);
        }
        setSearchParams(searchParams);
    };

    const clearFilters = () => {
        setFilters({
            listing_type: '',
            min_price: '',
            max_price: '',
            condition: '',
            sort_by: 'created_at',
            sort_order: 'desc',
        });
        setSearchParams({});
    };

    const hasActiveFilters = filters.listing_type || filters.min_price || filters.max_price || filters.condition;

    if (loading && listings.length === 0 && !category && !searchQuery) {
        return (
            <div className="min-h-screen bg-background py-8">
                <div className="container mx-auto px-4 lg:px-8">
                    <Skeleton className="h-12 w-64 mb-8" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                            <Skeleton key={i} className="h-[400px] rounded-3xl" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Generate structured data
    const categoryUrl = `https://colectarium.ro/category/${slug}${subcategory ? '/' + subcategory : ''}`;
    const categoryTitle = category?.name || (searchQuery ? `Căutare: ${searchQuery}` : 'Categorii');
    const categoryDescription = category 
        ? `Cumpără și vinde piese de ${category.name.toLowerCase()} pe Colectarium. ${totalItems} produse disponibile. Licitații și vânzări directe pentru colecționari.`
        : (searchQuery ? `${totalItems} rezultate pentru "${searchQuery}" pe Colectarium` : 'Explorează categoriile de piese de colecție');

    // Breadcrumb schema
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Acasă",
                "item": "https://colectarium.ro/"
            },
            ...(category ? [{
                "@type": "ListItem",
                "position": 2,
                "name": category.name,
                "item": `https://colectarium.ro/category/${slug}`
            }] : []),
            ...(subcategory && category ? [{
                "@type": "ListItem",
                "position": 3,
                "name": subcategory,
                "item": categoryUrl
            }] : [])
        ]
    };

    // CollectionPage schema for category
    const collectionSchema = category ? {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": category.name,
        "description": categoryDescription,
        "url": categoryUrl,
        "numberOfItems": totalItems,
        "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": totalItems,
            "itemListElement": listings.slice(0, 10).map((listing, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "url": `https://colectarium.ro/listing/${listing.slug || listing.listing_id}`,
                "name": listing.title
            }))
        }
    } : null;

    return (
        <div className="min-h-screen bg-background overflow-x-hidden" data-testid="category-page">
            {/* SEO Meta Tags with Helmet */}
            <Helmet>
                <title>{categoryTitle} - Piese de Colecție | Colectarium</title>
                <meta name="description" content={categoryDescription} />
                <link rel="canonical" href={categoryUrl} />
                
                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content={categoryUrl} />
                <meta property="og:title" content={`${categoryTitle} - Piese de Colecție | Colectarium`} />
                <meta property="og:description" content={categoryDescription} />
                <meta property="og:image" content="https://www.colectarium.ro/og-image.jpg" />
                <meta property="og:site_name" content="Colectarium" />
                <meta property="og:locale" content="ro_RO" />
                
                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${categoryTitle} | Colectarium`} />
                <meta name="twitter:description" content={categoryDescription} />
                <meta name="twitter:image" content="https://www.colectarium.ro/og-image.jpg" />
                
                {/* JSON-LD Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
                {collectionSchema && (
                    <script type="application/ld+json">
                        {JSON.stringify(collectionSchema)}
                    </script>
                )}
            </Helmet>

            {/* Header */}
            <div className="bg-white border-b border-border/50">
                <div className="container mx-auto px-4 lg:px-8 py-8">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                        <Link to="/" className="hover:text-primary">Acasă</Link>
                        {searchQuery ? (
                            <>
                                <span>/</span>
                                <span className="text-foreground">Căutare</span>
                            </>
                        ) : (
                            <>
                                <span>/</span>
                                <Link to={`/category/${slug}`} className="hover:text-primary capitalize">{category?.name || slug}</Link>
                                {subcategory && (
                                    <>
                                        <span>/</span>
                                        <span className="text-foreground capitalize">{subcategory}</span>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold">
                        {searchQuery ? (
                            <>Rezultate pentru &quot;<span className="text-primary">{searchQuery}</span>&quot;</>
                        ) : subcategory ? (
                            <span className="capitalize">{subcategory}</span>
                        ) : (
                            category?.name
                        )}
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        {totalItems} produse găsite
                    </p>
                </div>
            </div>

            {/* Sticky Filters Bar - Always visible */}
            <div className="sticky top-[112px] z-40 bg-white border-b border-border/50 shadow-xs" data-testid="filter-bar">
                <div className="container mx-auto px-4 lg:px-8">
                    {/* Subcategories - only on main category page, not for search */}
                    {!searchQuery && category?.subcategories?.length > 0 && !subcategory && (
                        <div className="flex flex-wrap gap-2 py-3 border-b border-border/30">
                            {category.subcategories.map((sub) => (
                                <Link
                                    key={sub.subcategory_id}
                                    to={`/category/${slug}/${sub.slug}`}
                                    className="px-4 py-2 rounded-full bg-gray-100 hover:bg-primary hover:text-white text-sm font-medium transition-colors"
                                >
                                    {sub.name}
                                </Link>
                            ))}
                        </div>
                    )}
                    
                    {/* Filters */}
                    <div className="py-3">
                        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
                            {/* Type Toggle */}
                            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                                <Button
                                    variant={filters.listing_type === '' ? 'default' : 'outline-solid'}
                                    size="sm"
                                    className="rounded-full text-xs sm:text-sm px-2 sm:px-3"
                                    onClick={() => handleFilterChange('listing_type', '')}
                                >
                                    Toate
                                </Button>
                                <Button
                                    variant={filters.listing_type === 'auction' ? 'default' : 'outline-solid'}
                                    size="sm"
                                    className="rounded-full text-xs sm:text-sm px-2 sm:px-3"
                                    onClick={() => handleFilterChange('listing_type', 'auction')}
                                    data-testid="filter-auctions"
                                >
                                    Licitații
                                </Button>
                                <Button
                                    variant={filters.listing_type === 'direct_sale' ? 'default' : 'outline-solid'}
                                    size="sm"
                                    className="rounded-full text-xs sm:text-sm px-2 sm:px-3"
                                    onClick={() => handleFilterChange('listing_type', 'direct_sale')}
                                    data-testid="filter-sales"
                                >
                                    Vânzări
                                </Button>
                            </div>

                        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                            {/* Sort */}
                            <Select
                                value={`${filters.sort_by}-${filters.sort_order}`}
                                onValueChange={(value) => {
                                    const [sort_by, sort_order] = value.split('-');
                                    handleFilterChange('sort_by', sort_by);
                                    handleFilterChange('sort_order', sort_order);
                                }}
                            >
                                <SelectTrigger className="w-[120px] sm:w-[180px] rounded-full text-xs sm:text-sm">
                                    <SelectValue placeholder="Sortare" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="created_at-desc">Cele mai noi</SelectItem>
                                    <SelectItem value="created_at-asc">Cele mai vechi</SelectItem>
                                    <SelectItem value="current_price-asc">Preț crescător</SelectItem>
                                    <SelectItem value="current_price-desc">Preț descrescător</SelectItem>
                                    <SelectItem value="bid_count-desc">Cele mai licitate</SelectItem>
                                </SelectContent>
                            </Select>

                            {/* More Filters Button */}
                            <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full text-xs sm:text-sm px-2 sm:px-3"
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <SlidersHorizontal className="w-4 h-4 sm:mr-2" />
                                <span className="hidden sm:inline">Filtre</span>
                                {hasActiveFilters && (
                                    <Badge className="ml-1 sm:ml-2 bg-primary text-white text-xs">
                                        {[filters.listing_type, filters.min_price, filters.max_price, filters.condition].filter(Boolean).length}
                                    </Badge>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Expanded Filters */}
                    {showFilters && (
                        <div className="mt-4 pt-4 border-t border-border/50 animate-slide-down">
                            <div className="flex flex-wrap items-end gap-4">
                                <div className="space-y-1">
                                    <label className="text-sm text-muted-foreground">Preț minim</label>
                                    <Input
                                        type="number"
                                        placeholder="0"
                                        value={filters.min_price}
                                        onChange={(e) => handleFilterChange('min_price', e.target.value)}
                                        className="w-32 rounded-xl"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm text-muted-foreground">Preț maxim</label>
                                    <Input
                                        type="number"
                                        placeholder="∞"
                                        value={filters.max_price}
                                        onChange={(e) => handleFilterChange('max_price', e.target.value)}
                                        className="w-32 rounded-xl"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm text-muted-foreground">Stare</label>
                                    <Select
                                        value={filters.condition || 'all'}
                                        onValueChange={(value) => handleFilterChange('condition', value === 'all' ? '' : value)}
                                    >
                                        <SelectTrigger className="w-[150px] rounded-xl">
                                            <SelectValue placeholder="Toate" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Toate</SelectItem>
                                            <SelectItem value="new">Nou</SelectItem>
                                            <SelectItem value="like_new">Ca nou</SelectItem>
                                            <SelectItem value="good">Bună</SelectItem>
                                            <SelectItem value="fair">Acceptabilă</SelectItem>
                                            <SelectItem value="poor">Slabă</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {hasActiveFilters && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="rounded-full text-red-500 hover:text-red-600 hover:bg-red-50"
                                        onClick={clearFilters}
                                    >
                                        <X className="w-4 h-4 mr-1" />
                                        Șterge filtrele
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                    </div>
                </div>
            </div>

            {/* Listings Grid */}
            <div className="container mx-auto px-4 lg:px-8 py-8">
                {loading && listings.length === 0 ? (
                    <div 
                        className="grid gap-6"
                        style={{
                            gridTemplateColumns: `repeat(${gridSettings.grid_columns_mobile}, minmax(0, 1fr))`,
                        }}
                    >
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                            <Skeleton key={i} className="h-[400px] rounded-3xl" />
                        ))}
                    </div>
                ) : listings.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                            <Search className="w-10 h-10 text-muted-foreground" />
                        </div>
                        <h3 className="font-heading text-xl font-semibold mb-2">Nu s-au găsit produse</h3>
                        <p className="text-muted-foreground mb-4">
                            Încearcă să modifici filtrele sau să cauți în altă categorie
                        </p>
                        {hasActiveFilters && (
                            <Button variant="outline" className="rounded-full" onClick={clearFilters}>
                                Șterge filtrele
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        <div 
                            className="grid gap-6 category-grid"
                            style={{
                                '--grid-cols-mobile': gridSettings.mobile,
                                '--grid-cols-tablet': gridSettings.tablet,
                                '--grid-cols-desktop': gridSettings.desktop,
                            }}
                        >
                            {listings.map((listing) => (
                                <ListingCard 
                                    key={listing.listing_id} 
                                    listing={listing} 
                                    singleColumn={singleColumn}
                                />
                            ))}
                        </div>
                        
                        {/* Infinite scroll loader */}
                        <div 
                            ref={loadMoreRef} 
                            className="flex justify-center py-8"
                            data-testid="infinite-scroll-trigger"
                        >
                            {loadingMore && (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    <span>Se încarcă...</span>
                                </div>
                            )}
                            {!hasMore && listings.length > 0 && (
                                <p className="text-muted-foreground text-sm">
                                    Ai văzut toate cele {totalItems} produse
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CategoryPage;
